import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import SEO from '../components/SEO';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useConfirm } from '../components/ConfirmModal';
import { isViaCoParent, entitledPlan } from '../utils/entitlements';

const SPECIES_SUGGESTIONS = [
  'Dog', 'Cat', 'Bunny', 'Bear', 'Fox', 'Dragon', 'Unicorn', 'Owl', 'Elephant',
  'Tiger', 'Penguin', 'Turtle', 'Fairy', 'Robot', 'Dinosaur', 'Kitten', 'Puppy',
];

const EMOJI_OPTIONS = [
  '🐾','🐶','🐱','🐰','🐻','🦊','🐲','🦄','🦉','🐘','🐯','🐧','🐢','🧚','🤖','🦕',
  '🐺','🦁','🐮','🐸','🐵','🦋','🐝','🦜','🦔','🦦','🐙','🦈','🐬','🌟',
];

const EMPTY_FORM = { name: '', species: '', emoji: '🐾', appearance: '', personality: '', backstory: '' };

export default function CharactersPage() {
  const { user } = useAuth();
  // characterController returns 403 to co-parents on create.
  const canCreate = !isViaCoParent(user);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [characters, setCharacters] = useState([]);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const isPaid = entitledPlan(user) !== 'free';

  const loadCharacters = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/characters');
      setLocked(res.data.locked || false);
      setCharacters(res.data.characters || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load characters.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login?redirect=/dashboard/characters'); return; }
    loadCharacters();
  }, [user, navigate, loadCharacters]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
    setShowEmojiPicker(false);
  };

  const openEdit = (char) => {
    setEditingId(char._id);
    setForm({
      name: char.name,
      species: char.species,
      emoji: char.emoji || '🐾',
      appearance: char.appearance || '',
      personality: char.personality || '',
      backstory: char.backstory || '',
    });
    setFormError('');
    setShowForm(true);
    setShowEmojiPicker(false);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    if (!form.species.trim()) { setFormError('Species / type is required.'); return; }
    setSaving(true);
    try {
      if (editingId) {
        const res = await API.put(`/characters/${editingId}`, form);
        setCharacters(prev => prev.map(c => c._id === editingId ? res.data.character : c));
      } else {
        const res = await API.post('/characters', form);
        setCharacters(prev => [res.data.character, ...prev]);
      }
      closeForm();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save character.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (char) => {
    const ok = await confirm({
      title: `Delete ${char.name}?`,
      message: `${char.name} will be removed from your character library. Existing stories won't be affected.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/characters/${char._id}`);
      setCharacters(prev => prev.filter(c => c._id !== char._id));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete character.');
    }
  };

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <DashboardLayout>
      <SEO title="Story Characters — Dreamy Bedtime" description="Create reusable pets and friends that appear in your child's bedtime stories." />

      {/* Page header */}
      <div className="rounded-4 p-4 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{ background: 'linear-gradient(135deg,#f0e8ff 0%,#e8d5ff 100%)', border: '1px solid #d4b8ff' }}>
        <div>
          <h4 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            🐾 Story Characters
          </h4>
          <p className="mb-0 small" style={{ color: '#6f42c1' }}>
            Recurring pets and friends that appear across your child's bedtime stories.
          </p>
        </div>
        {isPaid && !locked && canCreate && (
          <button className="btn btn-dream rounded-pill px-4 fw-semibold"
            onClick={openCreate}
            disabled={characters.length >= 10}>
            <i className="bi bi-plus-circle me-2"></i>New Character
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-4" style={{ fontSize: 14 }}>
          <i className="bi bi-exclamation-circle me-2"></i>{error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}></div>
          <p className="text-muted mt-3">Loading characters…</p>
        </div>

      /* Locked / upgrade */
      ) : locked ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '72px', marginBottom: '16px' }}>🐾</div>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Story Characters</h4>
          <p className="text-muted mb-4" style={{ maxWidth: 420, margin: '0 auto 24px' }}>
            Create reusable pets and friends that appear across multiple bedtime stories. Available on Basic and Family plans.
          </p>
          <div className="d-flex flex-wrap gap-2 justify-content-center mb-4">
            {['Buddy the Dog 🐶', 'Luna the Bunny 🐰', 'Spark the Dragon 🐲', 'Oliver the Bear 🐻'].map(ex => (
              <span key={ex} className="badge rounded-pill"
                style={{ background: 'var(--purple-section)', color: '#6f42c1', border: '1px solid var(--border-purple)', fontSize: 13, fontWeight: 500, padding: '6px 14px' }}>
                {ex}
              </span>
            ))}
          </div>
          <Link to="/pricing" className="btn btn-dream rounded-pill px-5 fw-bold">
            Upgrade to Unlock
          </Link>
        </div>

      /* Empty state */
      ) : characters.length === 0 && !showForm ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '72px', marginBottom: '16px' }}>🐾</div>
          <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>No characters yet</h4>
          <p className="text-muted mb-4" style={{ maxWidth: 420, margin: '0 auto 24px' }}>
            Create Buddy the Dog, Luna the Unicorn, or any pet or friend your child loves — they'll show up in every story you generate.
          </p>
          <div className="d-flex justify-content-center gap-4 mb-5 flex-wrap">
            {[['🎭', 'Recurring characters'], ['📖', 'Appear in any story'], ['✨', 'Fully customisable']].map(([icon, label]) => (
              <div key={label} className="text-center">
                <div className="fs-2 mb-1">{icon}</div>
                <div className="small fw-semibold" style={{ color: '#6f42c1' }}>{label}</div>
              </div>
            ))}
          </div>
          {canCreate && (
            <button className="btn btn-dream rounded-pill px-5 fw-bold" onClick={openCreate}>
              <i className="bi bi-plus-circle me-2"></i>Create First Character
            </button>
          )}
        </div>

      /* Character grid */
      ) : (
        <>
          <div className="row g-4 mb-4">
            {characters.map(char => (
              <div className="col-md-6 col-lg-4" key={char._id}>
                <div className="card border-0 shadow-sm h-100 card-hover"
                  style={{ borderRadius: '20px', overflow: 'hidden' }}>
                  {/* Colour band */}
                  <div style={{ height: '6px', background: 'linear-gradient(90deg,#6f42c1,#9b6fe8)' }}></div>
                  <div className="card-body p-4">

                    {/* Emoji + name */}
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle shadow-sm"
                        style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)', fontSize: '28px', flexShrink: 0 }}>
                        {char.emoji}
                      </div>
                      <div className="flex-grow-1 min-width-0">
                        <h5 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>{char.name}</h5>
                        <span className="text-muted small">{char.species}</span>
                      </div>
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-secondary rounded-circle"
                          style={{ width: '32px', height: '32px', padding: 0 }}
                          onClick={() => openEdit(char)} title="Edit">
                          <i className="bi bi-pencil" style={{ fontSize: '12px' }}></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger rounded-circle"
                          style={{ width: '32px', height: '32px', padding: 0 }}
                          onClick={() => handleDelete(char)} title="Delete">
                          <i className="bi bi-trash" style={{ fontSize: '12px' }}></i>
                        </button>
                      </div>
                    </div>

                    {/* Details */}
                    {char.appearance && (
                      <p className="small mb-1" style={{ color: 'var(--text-body)', lineHeight: 1.5 }}>
                        <span className="fw-semibold" style={{ color: 'var(--text-muted2)' }}>Looks: </span>
                        {char.appearance}
                      </p>
                    )}
                    {char.personality && (
                      <p className="small mb-1" style={{ color: 'var(--text-body)', lineHeight: 1.5 }}>
                        <span className="fw-semibold" style={{ color: 'var(--text-muted2)' }}>Personality: </span>
                        {char.personality}
                      </p>
                    )}

                    {/* Story count */}
                    {char.totalStories > 0 && (
                      <div className="border-top pt-2 mt-3">
                        <span className="small text-muted">
                          <i className="bi bi-book me-1"></i>
                          {char.totalStories} {char.totalStories === 1 ? 'story' : 'stories'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Add another card */}
            {characters.length < 10 && (
              <div className="col-md-6 col-lg-4">
                <div className="card border-0 h-100 d-flex align-items-center justify-content-center"
                  style={{ borderRadius: '20px', border: '2px dashed #c9a8ff', cursor: 'pointer', minHeight: '180px' }}
                  onClick={openCreate}>
                  <div className="text-center p-4">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                      style={{ width: '50px', height: '50px', background: 'var(--purple-section)' }}>
                      <i className="bi bi-plus-lg" style={{ color: '#6f42c1', fontSize: '20px' }}></i>
                    </div>
                    <div className="fw-semibold small" style={{ color: '#6f42c1' }}>Add another character</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {characters.length >= 10 && (
            <p className="text-muted small text-center mt-2">
              You've reached the 10-character limit.
            </p>
          )}
        </>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.55)', zIndex: 1050 }}
          onClick={e => { if (e.target === e.currentTarget) closeForm(); }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 520 }}>
            <div className="modal-content border-0" style={{ borderRadius: '24px' }}>

              <div className="modal-header border-0 px-4 pt-4 pb-2">
                <div>
                  <h5 className="modal-title fw-bold mb-0"
                    style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '22px' }}>
                    {editingId ? '✏️ Edit Character' : '✨ New Character'}
                  </h5>
                  <p className="text-muted small mb-0 mt-1">
                    {editingId ? 'Update this character\'s details.' : 'Give your character a name, look, and personality.'}
                  </p>
                </div>
                <button className="btn-close ms-auto" onClick={closeForm}></button>
              </div>

              <div className="modal-body px-4 pb-4">
                {formError && (
                  <div className="alert alert-danger py-2 small rounded-3 mb-3">{formError}</div>
                )}

                <form onSubmit={handleSave}>
                  {/* Emoji */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                      <i className="bi bi-emoji-smile me-1"></i>Choose an Emoji
                    </label>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <button type="button"
                        onClick={() => setShowEmojiPicker(p => !p)}
                        className="btn"
                        style={{ fontSize: '32px', background: 'var(--item-bg)', border: '2px solid var(--border-purple)', borderRadius: '12px', width: '56px', height: '56px', padding: 0 }}>
                        {form.emoji}
                      </button>
                      {showEmojiPicker && (
                        <div className="d-flex flex-wrap gap-1 p-2 rounded-3"
                          style={{ background: 'var(--header-bg)', border: '1px solid var(--border-purple)' }}>
                          {EMOJI_OPTIONS.map(em => (
                            <button key={em} type="button"
                              onClick={() => { updateForm('emoji', em); setShowEmojiPicker(false); }}
                              className="btn"
                              style={{ fontSize: '20px', background: form.emoji === em ? 'var(--purple-section)' : 'transparent', border: 'none', borderRadius: '6px', padding: '2px 5px' }}>
                              {em}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Name + Species */}
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                        Name <span className="text-danger">*</span>
                      </label>
                      <input className="form-control" placeholder="e.g. Buddy" maxLength={50}
                        value={form.name} onChange={e => updateForm('name', e.target.value)} required />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                        Species / Type <span className="text-danger">*</span>
                      </label>
                      <input className="form-control" list="species-list" placeholder="e.g. Dog" maxLength={50}
                        value={form.species} onChange={e => updateForm('species', e.target.value)} required />
                      <datalist id="species-list">
                        {SPECIES_SUGGESTIONS.map(s => <option key={s} value={s} />)}
                      </datalist>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                      Appearance <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <input className="form-control" maxLength={300}
                      placeholder="e.g. Golden fur, floppy ears, always wears a tiny red bow"
                      value={form.appearance} onChange={e => updateForm('appearance', e.target.value)} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                      Personality <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <input className="form-control" maxLength={300}
                      placeholder="e.g. Brave and loyal, loves snacks, scared of thunder"
                      value={form.personality} onChange={e => updateForm('personality', e.target.value)} />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-semibold small" style={{ color: 'var(--text-main)' }}>
                      Backstory <span className="text-muted fw-normal">(optional)</span>
                    </label>
                    <textarea className="form-control" maxLength={500} rows={3}
                      placeholder="e.g. Found as a puppy in the park, has been best friends with Mia ever since"
                      value={form.backstory} onChange={e => updateForm('backstory', e.target.value)} />
                  </div>

                  <div className="d-flex gap-2 justify-content-end pt-2">
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={closeForm}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-dream rounded-pill px-5 fw-semibold"
                      disabled={saving}>
                      {saving
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</>
                        : editingId ? '✓ Save Changes' : '✨ Create Character'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
